/**
 * Helpers for normalizing agent dynamic-variable payloads on the frontend.
 */

export interface AgentDynamicVariableDefinition {
  value: string;
}

export type AgentDynamicVariableValue = string | AgentDynamicVariableDefinition;

export const AGENT_RUNTIME_MANAGED_VARIABLE_PREFIXES = ['precall__'] as const;

/** Return whether a variable is produced by Contact-Tel runtime, not a caller. */
export function isAgentRuntimeManagedVariableKey(key: string): boolean {
  const normalizedKey = String(key || '').trim().toLowerCase();
  return AGENT_RUNTIME_MANAGED_VARIABLE_PREFIXES.some(
    (prefix) => normalizedKey.startsWith(prefix),
  );
}

export function normalizeAgentDynamicVariableDefinition(
  rawValue: unknown,
): AgentDynamicVariableDefinition {
  if (rawValue && typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    const payload = rawValue as Partial<AgentDynamicVariableDefinition>;
    return {
      value: payload.value == null ? '' : String(payload.value),
    };
  }

  return {
    value: rawValue == null ? '' : String(rawValue),
  };
}

export function normalizeAgentDynamicVariables(
  rawValues: unknown,
): Record<string, AgentDynamicVariableDefinition> {
  if (!rawValues || typeof rawValues !== 'object' || Array.isArray(rawValues)) {
    return {};
  }

  const normalized: Record<string, AgentDynamicVariableDefinition> = {};
  Object.entries(rawValues as Record<string, unknown>).forEach(([key, value]) => {
    const normalizedKey = key.trim();
    if (!normalizedKey) {
      return;
    }
    normalized[normalizedKey] = normalizeAgentDynamicVariableDefinition(value);
  });
  return normalized;
}

export function omitAgentDynamicVariables(
  rawValues: unknown,
  keysToOmit: Iterable<string>,
): Record<string, AgentDynamicVariableDefinition> {
  const normalized = normalizeAgentDynamicVariables(rawValues);
  const omittedKeys = new Set(
    Array.from(keysToOmit, (key) => String(key || '').trim()).filter(Boolean),
  );
  if (omittedKeys.size === 0) {
    return normalized;
  }
  return Object.fromEntries(
    Object.entries(normalized).filter(([key]) => !omittedKeys.has(key)),
  );
}

export function extractAgentDynamicVariableValues(
  rawValues: unknown,
): Record<string, string> {
  const normalized = normalizeAgentDynamicVariables(rawValues);
  return Object.fromEntries(
    Object.entries(normalized).map(([key, value]) => [key, value.value]),
  );
}

/**
 * Return only values that must be supplied before a call starts.
 *
 * Runtime-managed namespaces remain in the agent definition so prompts can
 * reference them, but campaign operators and manual callers must never be
 * asked to provide those outputs.
 */
export function extractAgentInputDynamicVariableValues(
  rawValues: unknown,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(extractAgentDynamicVariableValues(rawValues)).filter(
      ([key]) => !isAgentRuntimeManagedVariableKey(key),
    ),
  );
}

/** Return normalized input-variable definitions for campaign configuration. */
export function normalizeAgentInputDynamicVariables(
  rawValues: unknown,
): Record<string, AgentDynamicVariableDefinition> {
  return Object.fromEntries(
    Object.entries(normalizeAgentDynamicVariables(rawValues)).filter(
      ([key]) => !isAgentRuntimeManagedVariableKey(key),
    ),
  );
}
